/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useState } from "react";
import { v4 as uuid } from "uuid";

import { useTransition } from "../../../../../providers/TransitionRoot/TransitionRoot.emotion";
import type { TransitionKind } from "../Transition.emotion";
import { fade } from "./fade.emotion";
import { shape } from "./shape.emotion";
import { composeSlide, cover, getSlidePhase, push } from "./slide.emotion";
import {
  gradientComposer,
  maskPositionComposer,
  maskSizeComposer,
  wipe,
} from "./wipe.emotion";

export const useTransitionStyles = (kindProps: TransitionKind) => {
  const [uniqueTransitionId] = useState<string>(uuid());
  const { transitionMs } = useTransition();

  let baseCSS: Interpolation,
    enteringCSS: Interpolation,
    enteredCSS: Interpolation,
    exitingCSS: Interpolation,
    exitedCSS: Interpolation;
  switch (kindProps.kind) {
    case "push":
      baseCSS = [
        push.base(
          composeSlide(kindProps.direction, getSlidePhase("push", "base")),
        ),
      ];
      enteringCSS = [
        push.entering(
          composeSlide(kindProps.direction, getSlidePhase("push", "entering")),
        ),
      ];
      enteredCSS = [
        push.entered(
          composeSlide(kindProps.direction, getSlidePhase("push", "entered")),
        ),
      ];
      exitingCSS = [
        push.exiting(
          composeSlide(kindProps.direction, getSlidePhase("push", "exiting")),
        ),
      ];
      exitedCSS = [
        push.exited(
          composeSlide(kindProps.direction, getSlidePhase("push", "exited")),
        ),
      ];
      break;
    case "cover":
      baseCSS = [
        cover.base(
          composeSlide(kindProps.direction, getSlidePhase("cover", "base")),
        ),
      ];
      enteringCSS = [
        cover.entering(
          composeSlide(kindProps.direction, getSlidePhase("cover", "entering")),
        ),
      ];
      enteredCSS = [
        cover.entered(
          composeSlide(kindProps.direction, getSlidePhase("cover", "entered")),
        ),
      ];
      exitingCSS = [cover.exiting("")];
      exitedCSS = [cover.exited("")];

      break;
    case "wipe":
      baseCSS = [wipe.base];
      enteringCSS = [
        wipe.entering({
          maskImage: gradientComposer(kindProps.angle),
          maskSize: maskSizeComposer(kindProps.angle),
          maskPosition: maskPositionComposer(kindProps.angle, "start"),
        }),
      ];
      enteredCSS = [
        wipe.entered({
          maskImage: gradientComposer(kindProps.angle),
          maskSize: maskSizeComposer(kindProps.angle),
          maskPosition: maskPositionComposer(kindProps.angle, "end"),
        }),
      ];
      exitingCSS = [];
      exitedCSS = [];
      break;
    case "shape":
      baseCSS = [];
      enteringCSS = [];
      enteredCSS = [];
      exitingCSS = [shape.exiting];
      exitedCSS = [
        shape.exited({
          shape: kindProps.shape,
          duration: transitionMs,
          id: uniqueTransitionId,
        }),
      ];
      break;
    case "custom":
      baseCSS = kindProps.baseCSS ?? [];
      enteringCSS = kindProps.enteringCSS;
      enteredCSS = kindProps.enteredCSS;
      exitingCSS = kindProps.exitingCSS;
      exitedCSS = kindProps.exitedCSS;
      break;
    case "fade":
    default:
      baseCSS = [fade.base];
      enteringCSS = [fade.entering];
      enteredCSS = [fade.entered];
      exitingCSS = [fade.exiting];
      exitedCSS = [fade.exited];
  }

  return [baseCSS, enteringCSS, enteredCSS, exitingCSS, exitedCSS];
};
